import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { bestAlias } from "@/lib/pharmacology/aliasResolver";
import { ensureCanonicalSubstanceFromTemplate } from "@/lib/pharmacology/smartSubstance";
import { canonicalText, substanceTokens } from "@/lib/pharmacology/substanceCatalog";
import { savePatientMedication } from "@/lib/clinical/medicationPersistence";

export class ClinicalPersistenceError extends Error {
  table: string;
  op: string;
  supabaseMessage: string;
  payload: unknown;
  hint?: string;

  constructor(args: { table: string; op: string; supabaseMessage: string; payload: unknown; hint?: string }) {
    super(`[${args.op} ${args.table}] ${args.supabaseMessage}${args.hint ? ` — ${args.hint}` : ""}`);
    this.name = "ClinicalPersistenceError";
    this.table = args.table;
    this.op = args.op;
    this.supabaseMessage = args.supabaseMessage;
    this.payload = args.payload;
    this.hint = args.hint;
    console.error(`[clinicalPersistence] ${args.op} ${args.table} FAILED`, { message: args.supabaseMessage, payload: args.payload, hint: args.hint });
  }
}

function requireUser(user: User | null | undefined): asserts user is User {
  if (!user?.id) throw new Error("Sessão expirada. Faça login antes de gravar dados clínicos.");
}

function requireTimestamp(value: string | undefined | null, field: string): string {
  if (!value) throw new Error(`Data e horário clínicos são obrigatórios em ${field}.`);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) throw new Error(`Data/horário inválido em ${field}: ${value}`);
  return parsed.toISOString();
}

async function execute<T>(table: string, op: string, payload: unknown, fn: () => Promise<{ data: T | null; error: any }>): Promise<T> {
  const { data, error } = await fn();
  if (error) {
    throw new ClinicalPersistenceError({
      table,
      op,
      supabaseMessage: error.message ?? String(error),
      payload,
      hint: error.hint ?? error.details,
    });
  }
  return data as T;
}

async function listRows(table: string, configure?: (query: any) => any) {
  let query = (supabase as any).from(table).select("*");
  if (configure) query = configure(query);
  const { data, error } = await query;
  if (error) throw new ClinicalPersistenceError({ table, op: "select", supabaseMessage: error.message, payload: {} });
  return data ?? [];
}

export async function upsertPatientByName(user: User | null, fullName: string, extra: Record<string, any> = {}) {
  requireUser(user);
  const name = fullName.trim();
  if (!name) throw new Error("Nome do paciente é obrigatório.");
  const rows = await listRows("patients", (query) => query.eq("owner_id", user.id));
  const existing = rows.find((row: any) => canonicalText(row.full_name) === canonicalText(name));
  if (existing) return existing;
  return execute<any>("patients", "insert", { full_name: name }, () =>
    (supabase as any).from("patients").insert({ owner_id: user.id, full_name: name, status: "ativo", ...extra }).select("*").single(),
  );
}

export async function ensureCanonicalSubstance(user: User | null, params: {
  name: string;
  aliases?: string[];
  default_dose_unit?: string;
}) {
  requireUser(user);
  const name = params.name.trim();
  if (!name) throw new Error("Nome da substância é obrigatório.");

  const visible = await listRows("substances");
  const candidates = [name, ...(params.aliases ?? [])];
  const match = candidates.map((candidate) => bestAlias(candidate, 0.82)).find(Boolean);
  if (match?.template) {
    const ensured = await ensureCanonicalSubstanceFromTemplate(match.template, user, visible);
    return ensured.substance;
  }

  const wanted = new Set(candidates.map(canonicalText).filter(Boolean));
  const existing = visible.find((row: any) => substanceTokens(row).some((token) => wanted.has(canonicalText(token))));
  if (existing) return existing;

  return execute<any>("substances", "insert", { name, aliases: params.aliases }, () =>
    (supabase as any).from("substances").insert({
      owner_id: user.id,
      is_global: false,
      name,
      synonyms: params.aliases ?? [],
      default_dose_unit: params.default_dose_unit ?? "mg",
      dose_unit_default: params.default_dose_unit ?? "mg",
      requires_clinical_review: true,
      review_status: "não revisada",
      source_notes: "Criada manualmente pela camada de persistência; revisão clínica obrigatória.",
    }).select("*").single(),
  );
}

export async function ensureFormulation(user: User | null, params: {
  substance_id: string;
  formulation_name: string;
  route?: string;
  curve_model?: string | null;
  is_default?: boolean;
}) {
  requireUser(user);
  if (!params.substance_id) throw new Error("substance_id é obrigatório para criar formulação.");
  if (!params.formulation_name?.trim()) throw new Error("Nome da formulação é obrigatório.");
  const rows = await listRows("substance_formulations", (query) => query.eq("substance_id", params.substance_id));
  const existing = rows.find((row: any) => canonicalText(row.formulation_name) === canonicalText(params.formulation_name));
  if (existing) return existing;

  return execute<any>("substance_formulations", "insert", params, () =>
    (supabase as any).from("substance_formulations").insert({
      owner_id: user.id,
      substance_id: params.substance_id,
      formulation_name: params.formulation_name.trim(),
      formulation_type: params.formulation_name.trim(),
      route: params.route ?? "oral",
      curve_model: params.curve_model ?? null,
      is_default: params.is_default ?? false,
      notes: "Formulação criada pela camada de persistência; revisar PK/PD.",
    }).select("*").single(),
  );
}

export interface CreatePatientMedicationInput {
  patient_id: string;
  substance_id?: string | null;
  formulation_id?: string | null;
  display_name: string;
  canonical_name?: string;
  current_dose?: number | null;
  dose_unit?: string;
  frequency?: string | null;
  usual_time?: string | null;
  status?: string;
  start_date?: string | null;
  clinical_rationale?: string | null;
  monitoring_plan?: string | null;
  patient_notes?: string | null;
}

export async function createPatientMedicationFromCanonical(user: User | null, input: CreatePatientMedicationInput) {
  requireUser(user);
  const result = await savePatientMedication({
    owner_id: user.id,
    patient_id: input.patient_id,
    substance_id: input.substance_id ?? null,
    formulation_id: input.formulation_id ?? null,
    brand_name: input.display_name,
    free_text_name: input.canonical_name ? `${input.display_name} → ${input.canonical_name}` : input.display_name,
    current_dose: input.current_dose ?? null,
    dose_unit: input.dose_unit ?? "mg",
    frequency: input.frequency ?? null,
    usual_time: input.usual_time ?? null,
    status: input.status ?? "ativo",
    start_date: input.start_date ?? null,
    clinical_rationale: input.clinical_rationale ?? null,
    monitoring_plan: input.monitoring_plan ?? null,
    patient_notes: input.patient_notes ?? null,
  });
  return result.row;
}

export async function createTargetSymptom(user: User | null, input: {
  patient_id: string;
  symptom_name: string;
  baseline?: number | null;
  therapeutic_goal?: number | null;
  priority?: number;
  related_diagnosis?: string | null;
}) {
  requireUser(user);
  if (!input.patient_id) throw new Error("patient_id é obrigatório.");
  if (!input.symptom_name?.trim()) throw new Error("Nome do sintoma-alvo é obrigatório.");
  const rows = await listRows("patient_target_symptoms", (query) => query.eq("owner_id", user.id).eq("patient_id", input.patient_id));
  const existing = rows.find((row: any) => canonicalText(row.symptom_name) === canonicalText(input.symptom_name));
  if (existing) return existing;
  const payload = {
    owner_id: user.id,
    patient_id: input.patient_id,
    symptom_name: input.symptom_name.trim(),
    baseline: input.baseline ?? null,
    therapeutic_goal: input.therapeutic_goal ?? null,
    priority: input.priority ?? 3,
    related_diagnosis: input.related_diagnosis ?? null,
    status: "ativo",
  };
  return execute<any>("patient_target_symptoms", "insert", payload, () =>
    (supabase as any).from("patient_target_symptoms").insert(payload).select("*").single(),
  );
}

export async function createDoseLog(user: User | null, input: {
  patient_id: string;
  patient_medication_id?: string | null;
  substance_id?: string | null;
  formulation_id?: string | null;
  actual_time: string;
  dose_amount?: number | null;
  dose_unit?: string | null;
  extra?: Record<string, any>;
}) {
  requireUser(user);
  if (!input.patient_id) throw new Error("patient_id é obrigatório.");
  const actualTime = requireTimestamp(input.actual_time, "actual_time");
  const rows = await listRows("medication_dose_logs", (query) => query.eq("owner_id", user.id).eq("patient_id", input.patient_id).eq("actual_time", actualTime));
  const existing = rows.find((row: any) =>
    String(row.patient_medication_id ?? "") === String(input.patient_medication_id ?? "")
    && Number(row.dose_amount ?? 0) === Number(input.dose_amount ?? 0),
  );
  if (existing) return existing;
  const payload = {
    owner_id: user.id,
    patient_id: input.patient_id,
    patient_medication_id: input.patient_medication_id ?? null,
    substance_id: input.substance_id ?? null,
    formulation_id: input.formulation_id ?? null,
    actual_time: actualTime,
    dose_amount: input.dose_amount ?? null,
    dose_unit: input.dose_unit ?? null,
    ...(input.extra ?? {}),
  };
  return execute<any>("medication_dose_logs", "insert", payload, () =>
    (supabase as any).from("medication_dose_logs").insert(payload).select("*").single(),
  );
}

export async function createClinicalSession(user: User | null, input: {
  patient_id: string;
  session_at: string;
  session_type?: string;
  session_status?: string;
  is_retroactive?: boolean;
  notes?: string | null;
  name?: string | null;
}) {
  requireUser(user);
  if (!input.patient_id) throw new Error("patient_id é obrigatório.");
  const sessionAt = requireTimestamp(input.session_at, "session_at");
  const rows = await listRows("clinical_sessions", (query) => query.eq("owner_id", user.id).eq("patient_id", input.patient_id).eq("session_at", sessionAt));
  const existing = rows.find((row: any) => canonicalText(row.session_type) === canonicalText(input.session_type ?? "retorno"));
  if (existing) return existing;
  const payload = {
    owner_id: user.id,
    patient_id: input.patient_id,
    name: input.name ?? `${input.session_type ?? "Retorno"} — ${new Date(sessionAt).toLocaleDateString("pt-BR")}`,
    session_at: sessionAt,
    scheduled_start_at: sessionAt,
    session_type: input.session_type ?? "retorno",
    session_status: input.session_status ?? "realizada",
    status: input.session_status ?? "realizada",
    is_retroactive: input.is_retroactive ?? false,
    notes: input.notes ?? null,
  };
  return execute<any>("clinical_sessions", "insert", payload, () =>
    (supabase as any).from("clinical_sessions").insert(payload).select("*").single(),
  );
}

export async function updateClinicalSessionDateTime(user: User | null, id: string, sessionAt: string, extra: Record<string, any> = {}) {
  requireUser(user);
  const timestamp = requireTimestamp(sessionAt, "session_at");
  const payload = { session_at: timestamp, scheduled_start_at: timestamp, ...extra };
  return execute<any>("clinical_sessions", "update", { id, ...payload }, () =>
    (supabase as any).from("clinical_sessions").update(payload).eq("id", id).eq("owner_id", user.id).select("*").single(),
  );
}

export async function createPsyLogEvent(user: User | null, input: {
  patient_id: string;
  event_at: string;
  event_type: string;
  title?: string | null;
  description?: string | null;
  intensity_0_10?: number | null;
  intensity_0_100?: number | null;
  linked_medication_id?: string | null;
  linked_dose_id?: string | null;
  linked_session_id?: string | null;
  metadata?: Record<string, any>;
}) {
  requireUser(user);
  if (!input.patient_id) throw new Error("patient_id é obrigatório.");
  if (!input.event_type?.trim()) throw new Error("event_type é obrigatório.");
  const eventAt = requireTimestamp(input.event_at, "event_at");
  const rows = await listRows("patient_psylog_events", (query) => query.eq("owner_id", user.id).eq("patient_id", input.patient_id).eq("event_at", eventAt));
  const existing = rows.find((row: any) => canonicalText(row.event_type) === canonicalText(input.event_type) && canonicalText(row.title) === canonicalText(input.title));
  if (existing) return existing;
  const payload = {
    owner_id: user.id,
    patient_id: input.patient_id,
    event_at: eventAt,
    event_type: input.event_type,
    title: input.title ?? null,
    description: input.description ?? null,
    intensity_0_10: input.intensity_0_10 ?? null,
    intensity_0_100: input.intensity_0_100 ?? null,
    linked_medication_id: input.linked_medication_id ?? null,
    linked_dose_id: input.linked_dose_id ?? null,
    linked_session_id: input.linked_session_id ?? null,
    metadata: input.metadata ?? {},
  };
  return execute<any>("patient_psylog_events", "insert", payload, () =>
    (supabase as any).from("patient_psylog_events").insert(payload).select("*").single(),
  );
}

export async function createVitalSign(user: User | null, input: {
  patient_id: string;
  measured_at: string;
  systolic_bp?: number | null;
  diastolic_bp?: number | null;
  heart_rate?: number | null;
  weight_kg?: number | null;
  oxygen_saturation?: number | null;
  context?: string | null;
  linked_dose_id?: string | null;
  notes?: string | null;
}) {
  requireUser(user);
  if (!input.patient_id) throw new Error("patient_id é obrigatório.");
  const measuredAt = requireTimestamp(input.measured_at, "measured_at");
  const rows = await listRows("patient_vital_signs", (query) => query.eq("owner_id", user.id).eq("patient_id", input.patient_id).eq("measured_at", measuredAt));
  if (rows[0]) return rows[0];
  const payload = { owner_id: user.id, ...input, measured_at: measuredAt };
  return execute<any>("patient_vital_signs", "insert", payload, () =>
    (supabase as any).from("patient_vital_signs").insert(payload).select("*").single(),
  );
}

export function parseCsv(text: string): Record<string, string>[] {
  const lines = text.replace(/\r/g, "").split("\n").filter((line) => line.trim().length > 0);
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    headers.forEach((header, index) => { row[header.trim()] = (cells[index] ?? "").trim(); });
    return row;
  });
}

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index++) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') { current += '"'; index++; }
      else quoted = !quoted;
      continue;
    }
    if (character === "," && !quoted) { result.push(current); current = ""; continue; }
    current += character;
  }
  result.push(current);
  return result;
}

function numberOrNull(value: string): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function integerOrNull(value: string): number | null {
  const parsed = numberOrNull(value);
  return parsed == null ? null : Math.round(parsed);
}

function combineTimestamp(row: Record<string, string>, direct: string, dateField: string, timeField: string) {
  if (row[direct]) return row[direct];
  if (!row[dateField]) return "";
  return `${row[dateField]}T${row[timeField] || "12:00"}:00`;
}

async function resolvePatientId(user: User, row: Record<string, string>): Promise<string> {
  if (row.patient_id) return row.patient_id;
  if (row.patient_name) return (await upsertPatientByName(user, row.patient_name)).id;
  throw new Error("Linha sem patient_id nem patient_name.");
}

export interface ImportResult {
  inserted: number;
  failed: number;
  errors: { row: number; message: string }[];
}

export async function importPsyLogCsv(user: User | null, text: string): Promise<ImportResult> {
  requireUser(user);
  const rows = parseCsv(text);
  const errors: ImportResult["errors"] = [];
  let inserted = 0;
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    try {
      const patientId = await resolvePatientId(user, row);
      await createPsyLogEvent(user, {
        patient_id: patientId,
        event_at: combineTimestamp(row, "event_at", "event_date", "event_time"),
        event_type: row.event_type || "nota",
        title: row.title || null,
        description: row.description || null,
        intensity_0_10: numberOrNull(row.intensity_0_10),
        intensity_0_100: integerOrNull(row.intensity_0_100),
        linked_medication_id: row.linked_medication_id || null,
        linked_dose_id: row.linked_dose_id || null,
        linked_session_id: row.linked_session_id || null,
        metadata: row.metadata ? safeJson(row.metadata) : {},
      });
      inserted++;
    } catch (error: any) {
      errors.push({ row: index + 2, message: error?.message ?? String(error) });
    }
  }
  return { inserted, failed: errors.length, errors };
}

export async function importVitalSignsCsv(user: User | null, text: string): Promise<ImportResult> {
  requireUser(user);
  const rows = parseCsv(text);
  const errors: ImportResult["errors"] = [];
  let inserted = 0;
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    try {
      const patientId = await resolvePatientId(user, row);
      await createVitalSign(user, {
        patient_id: patientId,
        measured_at: combineTimestamp(row, "measured_at", "measured_date", "measured_time"),
        systolic_bp: integerOrNull(row.systolic_bp),
        diastolic_bp: integerOrNull(row.diastolic_bp),
        heart_rate: integerOrNull(row.heart_rate),
        weight_kg: numberOrNull(row.weight_kg),
        oxygen_saturation: integerOrNull(row.oxygen_saturation),
        context: row.context || null,
        notes: row.notes || null,
      });
      inserted++;
    } catch (error: any) {
      errors.push({ row: index + 2, message: error?.message ?? String(error) });
    }
  }
  return { inserted, failed: errors.length, errors };
}

export async function importSessionsCsv(user: User | null, text: string): Promise<ImportResult> {
  requireUser(user);
  const rows = parseCsv(text);
  const errors: ImportResult["errors"] = [];
  let inserted = 0;
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    try {
      const patientId = await resolvePatientId(user, row);
      await createClinicalSession(user, {
        patient_id: patientId,
        session_at: combineTimestamp(row, "session_at", "session_date", "session_time"),
        session_type: row.session_type || "retorno",
        session_status: row.session_status || "realizada",
        is_retroactive: row.is_retroactive === "true" || row.is_retroactive === "1",
        notes: row.notes || null,
        name: row.name || null,
      });
      inserted++;
    } catch (error: any) {
      errors.push({ row: index + 2, message: error?.message ?? String(error) });
    }
  }
  return { inserted, failed: errors.length, errors };
}

export async function importTargetSymptomsCsv(user: User | null, text: string): Promise<ImportResult> {
  requireUser(user);
  const rows = parseCsv(text);
  const errors: ImportResult["errors"] = [];
  let inserted = 0;
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    try {
      const patientId = await resolvePatientId(user, row);
      await createTargetSymptom(user, {
        patient_id: patientId,
        symptom_name: row.symptom_name,
        baseline: numberOrNull(row.baseline),
        therapeutic_goal: numberOrNull(row.therapeutic_goal),
        priority: integerOrNull(row.priority) ?? 3,
        related_diagnosis: row.related_diagnosis || null,
      });
      inserted++;
    } catch (error: any) {
      errors.push({ row: index + 2, message: error?.message ?? String(error) });
    }
  }
  return { inserted, failed: errors.length, errors };
}

function safeJson(value: string): Record<string, any> {
  try { return JSON.parse(value); }
  catch { return { raw: value }; }
}
